/**
 * Terminal 82 - Card Utilities
 *
 * Helper functions for formatting, calculations, and data processing.
 */

import type { CandleDirection, RarityTier, MythicEvent } from '../types';

// Tier-based minimum candle heights (ensures rare cards look impressive)
const TIER_MIN_HEIGHTS: Record<RarityTier, number> = {
  common: 60,
  uncommon: 70,
  rare: 80,
  epic: 100,
  legendary: 120,
  mythic: 140,
};
import { TIER_LABELS, MYTHIC_EVENT_LABELS } from '../types';

// ============================================
// Date Utilities
// ============================================

/**
 * ETH Genesis block timestamp: July 30, 2015 15:26:13 UTC
 * Must match contract's ETH_GENESIS constant
 */
const ETH_GENESIS = 1438269973;

/**
 * Calculate day ID (days since ETH genesis)
 * Matches contract: ((timestamp - ETH_GENESIS) / 1 days) + 1
 */
export function calculateDayId(dateString: string): number {
  const [year, month, day] = dateString.split('-').map(Number);
  // Use midday UTC to get the day that contains this calendar date
  const target = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const timestamp = Math.floor(target.getTime() / 1000);
  return Math.floor((timestamp - ETH_GENESIS) / 86400) + 1;
}

/**
 * Format date for display: "DEC 06 2024"
 */
export function formatDisplayDate(date: string): string {
  const d = new Date(date + 'T00:00:00Z'); // Parse as UTC
  const months = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
  ];
  const month = months[d.getUTCMonth()];
  const day = String(d.getUTCDate()).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${month} ${day} ${year}`;
}

/**
 * Format day ID with hash: "#0341"
 */
export function formatDayId(dayId: number): string {
  return `#${String(dayId).padStart(4, '0')}`;
}

// ============================================
// Price Formatting
// ============================================

/**
 * Format price with appropriate precision
 */
export function formatPrice(price: number): string {
  if (price >= 1000) {
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (price >= 100) {
    return price.toFixed(2);
  }
  if (price >= 1) {
    return price.toFixed(2);
  }
  return price.toFixed(4);
}

/**
 * Format price for OHLC display (no decimals for large values)
 */
export function formatOHLCPrice(price: number): string {
  if (price >= 1000) {
    return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (price >= 100) {
    return Math.round(price).toString();
  }
  return price.toFixed(1);
}

/**
 * Format percentage change: "+3.44%" or "-43.28%"
 */
export function formatPercentChange(percent: number): string {
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(2)}%`;
}

/**
 * Format large numbers: "$462B", "18.4B"
 */
export function formatLargeNumber(value: number, prefix = ''): string {
  if (value >= 1e12) {
    return `${prefix}${(value / 1e12).toFixed(1)}T`;
  }
  if (value >= 1e9) {
    return `${prefix}${(value / 1e9).toFixed(1)}B`;
  }
  if (value >= 1e6) {
    return `${prefix}${(value / 1e6).toFixed(1)}M`;
  }
  return `${prefix}${value.toLocaleString()}`;
}

// ============================================
// Y-Axis Calculation
// ============================================

/**
 * Round to nearest "nice" number for Y-axis
 */
function niceRound(value: number, step: number): number {
  return Math.round(value / step) * step;
}

/**
 * Determine nice step size based on price range
 */
function getNiceStep(range: number): number {
  if (range > 1000) return 100;
  if (range > 500) return 50;
  if (range > 100) return 20;
  if (range > 50) return 10;
  if (range > 10) return 5;
  return 1;
}

export interface YAxisInfo {
  labels: string[];
  max: number;
  min: number;
}

/**
 * Generate Y-axis labels and range for chart
 */
export function generateYAxisInfo(ohlc: { high: number; low: number }): YAxisInfo {
  const range = ohlc.high - ohlc.low || 1;
  const step = getNiceStep(range);

  // Round to nice values with 5% padding
  const max = niceRound(ohlc.high + range * 0.05, step);
  const min = niceRound(ohlc.low - range * 0.05, step);
  const labelStep = (max - min) / 3;

  return {
    labels: [
      formatOHLCPrice(max),
      formatOHLCPrice(niceRound(max - labelStep, step)),
      formatOHLCPrice(niceRound(max - labelStep * 2, step)),
      formatOHLCPrice(min),
    ],
    max,
    min,
  };
}

/**
 * Generate Y-axis labels for chart (legacy function)
 */
export function generateYAxisLabels(ohlc: { high: number; low: number }): string[] {
  return generateYAxisInfo(ohlc).labels;
}

// ============================================
// Tier & Direction Logic
// ============================================

/**
 * Determine candle direction from price change
 */
export function determineDirection(changePercent: number): CandleDirection {
  return changePercent >= 0 ? 'bullish' : 'bearish';
}

/**
 * Get display text for tier or mythic event
 */
export function getTierText(tier: RarityTier, mythicEvent?: MythicEvent): string {
  if (mythicEvent) {
    return MYTHIC_EVENT_LABELS[mythicEvent].toUpperCase();
  }
  return TIER_LABELS[tier].toUpperCase();
}

// ============================================
// Candle Sizing & Positioning
// ============================================

export interface CandleDimensions {
  bodyHeight: number;
  wickTop: number;
  wickBottom: number;
  width: number;
  depth: number;
}

export interface CandlePositioning {
  // Pixel positions from top of chart grid
  topY: number;      // Top of top wick (High price)
  bodyTopY: number;  // Top of body (max of O,C)
  bodyBottomY: number; // Bottom of body (min of O,C)
  bottomY: number;   // Bottom of bottom wick (Low price)
  // Calculated dimensions
  wickTopHeight: number;
  bodyHeight: number;
  wickBottomHeight: number;
  totalHeight: number;
  width: number;
  depth: number;
}

// Chart grid height constant (matches CSS)
const CHART_GRID_HEIGHT = 180;

/**
 * Calculate candle positioning aligned to Y-axis scale
 *
 * This ensures the candle visually aligns with the Y-axis labels,
 * so High price is at the top and Low price is at the bottom.
 */
export function calculateCandlePositioning(
  ohlc: { open: number; high: number; low: number; close: number },
  yAxis: { max: number; min: number }
): CandlePositioning {
  const yRange = yAxis.max - yAxis.min || 1;

  // Convert price to Y pixel position (0 = top, CHART_GRID_HEIGHT = bottom)
  const priceToY = (price: number): number => {
    return CHART_GRID_HEIGHT * (1 - (price - yAxis.min) / yRange);
  };

  // Calculate Y positions for each part of the candle
  const topY = priceToY(ohlc.high);
  const bottomY = priceToY(ohlc.low);
  const bodyTopY = priceToY(Math.max(ohlc.open, ohlc.close));
  const bodyBottomY = priceToY(Math.min(ohlc.open, ohlc.close));

  // Calculate dimensions from positions
  let wickTopHeight = bodyTopY - topY;
  let bodyHeight = bodyBottomY - bodyTopY;
  let wickBottomHeight = bottomY - bodyBottomY;

  // Apply minimums for visibility
  const minBody = 8;
  const minWick = 4;

  if (bodyHeight < minBody && bodyHeight > 0) {
    bodyHeight = minBody;
  }
  if (wickTopHeight > 0 && wickTopHeight < minWick) {
    wickTopHeight = minWick;
  }
  if (wickBottomHeight > 0 && wickBottomHeight < minWick) {
    wickBottomHeight = minWick;
  }

  const totalHeight = wickTopHeight + bodyHeight + wickBottomHeight;

  // Width based on body ratio (conviction indicator)
  const range = ohlc.high - ohlc.low || 1;
  const bodyRatio = Math.abs(ohlc.close - ohlc.open) / range;
  const width = Math.round(40 + bodyRatio * 25);
  const depth = Math.round(width * 0.5);

  return {
    topY,
    bodyTopY,
    bodyBottomY,
    bottomY,
    wickTopHeight: Math.round(wickTopHeight),
    bodyHeight: Math.round(bodyHeight),
    wickBottomHeight: Math.round(wickBottomHeight),
    totalHeight: Math.round(totalHeight),
    width,
    depth,
  };
}

/**
 * Calculate candle dimensions for NFT card display
 *
 * OHLC-PROPORTIONAL + TIER-BASED SIZING:
 * - Shape (OHLC ratios) is always accurate to real price action
 * - Size is the larger of volatility-based or tier minimum
 * - Ensures rare cards look impressive while keeping accuracy
 *
 * TIER MINIMUMS:
 * | Tier      | Min Height |
 * |-----------|------------|
 * | Common    | 60px       |
 * | Uncommon  | 70px       |
 * | Rare      | 80px       |
 * | Epic      | 100px      |
 * | Legendary | 120px      |
 * | Mythic    | 140px      |
 *
 * VOLATILITY adds height: 60px base + 10px per 1% range
 * Final height = max(volatility, tier minimum), capped at 220px
 *
 * DISTRIBUTION (from OHLC ratios):
 * - Body, top wick, bottom wick sized proportionally to actual price action
 * - Minimums applied only for visibility (10px body, 5px wicks)
 */
export function calculateCandleDimensions(
  ohlc: { open: number; high: number; low: number; close: number },
  _changePercent: number,
  tier: RarityTier = 'common'
): CandleDimensions {
  // === OHLC Structure Analysis ===
  const range = ohlc.high - ohlc.low || 1;
  const bodySize = Math.abs(ohlc.close - ohlc.open);
  const bodyTop = Math.max(ohlc.open, ohlc.close);
  const bodyBottom = Math.min(ohlc.open, ohlc.close);

  // Calculate OHLC ratios (these determine the SHAPE)
  const bodyRatio = bodySize / range;
  const topWickRatio = (ohlc.high - bodyTop) / range;
  const bottomWickRatio = (bodyBottom - ohlc.low) / range;

  // === TOTAL HEIGHT FROM VOLATILITY + TIER MINIMUM ===
  // Volatility determines base size, tier sets minimum
  const price = ohlc.close || ohlc.open || 1;
  const rangePercent = (range / price) * 100;
  const tierMinHeight = TIER_MIN_HEIGHTS[tier];

  // Volatility-based height: 60px base + 10px per 1% range
  const volatilityHeight = 60 + rangePercent * 10;
  // Use the larger of volatility-based or tier minimum, capped at 220px
  const totalHeight = Math.min(220, Math.max(volatilityHeight, tierMinHeight));

  // === DISTRIBUTE HEIGHT ACCORDING TO OHLC RATIOS ===
  let bodyHeight = totalHeight * bodyRatio;
  let wickTop = totalHeight * topWickRatio;
  let wickBottom = totalHeight * bottomWickRatio;

  // === APPLY MINIMUMS FOR VISIBILITY ===
  // Ensure body is always visible (min 10px)
  const minBody = 10;
  if (bodyHeight < minBody && bodyRatio > 0) {
    const deficit = minBody - bodyHeight;
    bodyHeight = minBody;
    // Reduce wicks proportionally to compensate
    const totalWick = wickTop + wickBottom;
    if (totalWick > deficit) {
      const wickScale = (totalWick - deficit) / totalWick;
      wickTop *= wickScale;
      wickBottom *= wickScale;
    }
  }

  // Ensure wicks are visible when they exist (min 5px)
  if (topWickRatio > 0.001 && wickTop < 5) {
    wickTop = 5;
  } else if (topWickRatio <= 0.001) {
    wickTop = 0;
  }

  if (bottomWickRatio > 0.001 && wickBottom < 5) {
    wickBottom = 5;
  } else if (bottomWickRatio <= 0.001) {
    wickBottom = 0;
  }

  // === FINAL CAP TO FIT CARD ===
  const finalTotal = bodyHeight + wickTop + wickBottom;
  if (finalTotal > 220) {
    const scale = 220 / finalTotal;
    bodyHeight *= scale;
    wickTop *= scale;
    wickBottom *= scale;
  }

  // === Width from Body Ratio (Conviction) ===
  // Wider body = more conviction (larger O-C relative to range)
  const width = Math.round(40 + bodyRatio * 25);

  // === Depth for 3D ===
  const depth = Math.round(width * 0.5);

  return {
    bodyHeight: Math.round(bodyHeight),
    wickTop: Math.round(wickTop),
    wickBottom: Math.round(wickBottom),
    width,
    depth,
  };
}

/**
 * Legacy function for backward compatibility
 */
export function calculateCandleHeight(
  ohlc: { open: number; high: number; low: number; close: number },
  changePercent: number
): number {
  return calculateCandleDimensions(ohlc, changePercent).bodyHeight;
}

/**
 * Legacy wick function
 */
export function calculateWickHeights(
  ohlc: { open: number; high: number; low: number; close: number },
  _bodyHeight?: number
): { top: number; bottom: number } {
  const dims = calculateCandleDimensions(ohlc, 0);
  return { top: dims.wickTop, bottom: dims.wickBottom };
}

// ============================================
// Volume Bar Heights
// ============================================

/**
 * Normalize volume data to percentage heights
 */
export function normalizeVolumeData(volumes: number[]): number[] {
  const max = Math.max(...volumes);
  if (max === 0) return volumes.map(() => 20);
  return volumes.map((v) => Math.max(15, (v / max) * 100));
}

/**
 * Get volume bar intensity class
 */
export function getVolumeIntensityClass(percentHeight: number): string {
  if (percentHeight >= 95) return 'imax';
  if (percentHeight >= 80) return 'i5';
  if (percentHeight >= 60) return 'i4';
  if (percentHeight >= 40) return 'i3';
  if (percentHeight >= 25) return 'i2';
  return 'i1';
}
